import { create } from 'zustand'
import type { FileStat } from 'webdav'
import { createDavClient, davPut, joinDavPath, parentDavPath, targetKey } from '../lib/webdavClient'
import type { FileTarget } from '../lib/webdavClient'
import { createId } from '../utilities/general'

export interface FileEntry {
  basename: string
  filename: string // full path from the network dav root, e.g. "/foo/bar.txt"
  isDirectory: boolean
  size: number
  lastmod: string
}

export interface UploadState {
  id: string
  basename: string
  bytesUploaded: number
  bytesTotal: number
}

export interface FilesState {
  entries: Record<string, FileEntry[]>
  loading: Record<string, boolean>
  error: Record<string, string | null>
  uploads: Record<string, UploadState[]>
  // Total stored bytes per TARGET (keyed by targetKey, not by directory like the
  // maps above) — what the node and network pages print beside their Files link.
  sizes: Record<string, number>
  // The id of the walk currently out for a target, if any — an id rather than a
  // flag so a walk can tell whether the total it is holding is still the one
  // this target is waiting for.
  sizesLoading: Record<string, number>
  list: (target: FileTarget, path: string) => Promise<void>
  refresh: (target: FileTarget, path: string) => Promise<void>
  loadSize: (target: FileTarget) => Promise<void>
  upload: (target: FileTarget, path: string, file: File) => Promise<void>
  download: (target: FileTarget, filePath: string, basename: string) => Promise<void>
  remove: (target: FileTarget, filePath: string) => Promise<void>
  createFolder: (target: FileTarget, path: string, name: string) => Promise<void>
  rename: (target: FileTarget, filePath: string, newBasename: string) => Promise<void>
  reset: () => void
}

const key = (target: FileTarget, path: string) => `${targetKey(target)}:${joinDavPath(path)}`

// A ceiling on the size walk. The router walks the tree directory by directory
// through the mount, so a deep enough share can take arbitrarily long — and
// nothing on the page waits for this number. Give up rather than hold a
// connection open behind a figure that is decoration.
const SIZE_WALK_TIMEOUT_MS = 15_000

let sizeWalkSeq = 0

// Forget a target's cached total, so the next page view re-walks the tree.
// Called wherever bytes move; a rename or a new (empty) folder moves none.
//
// ⚠ Forgetting the walk in flight is half the job: a walk that started before
// these bytes moved counted the tree WITHOUT them, and writing that total on
// return would put a wrong number in a cache nothing refreshes for the rest of
// the session. Clearing the id is what makes it discard its result instead.
const dropSize = (state: FilesState, target: FileTarget): Pick<FilesState, 'sizes' | 'sizesLoading'> => {
  const k = targetKey(target)
  const sizes = { ...state.sizes }
  const sizesLoading = { ...state.sizesLoading }
  delete sizes[k]
  delete sizesLoading[k]
  return { sizes, sizesLoading }
}

const toEntry = (stat: FileStat): FileEntry => ({
  basename: stat.basename,
  filename: stat.filename,
  isDirectory: stat.type === 'directory',
  size: stat.size ?? 0,
  lastmod: stat.lastmod ?? '',
})

const useFiles = create<FilesState>()((set, get) => ({
  entries: {},
  loading: {},
  error: {},
  uploads: {},
  sizes: {},
  sizesLoading: {},
  list: async (target, path) => {
    const k = key(target, path)
    // Cached — caller uses refresh() to force.
    if (get().entries[k] && !get().error[k]) return
    await get().refresh(target, path)
  },
  refresh: async (target, path) => {
    const k = key(target, path)
    set(state => ({
      loading: { ...state.loading, [k]: true },
      error: { ...state.error, [k]: null },
    }))
    try {
      const client = createDavClient(target)
      const stats = await client.getDirectoryContents(joinDavPath(path), { deep: false })
      const entries = stats.map(toEntry)
      set(state => ({
        entries: { ...state.entries, [k]: entries },
        loading: { ...state.loading, [k]: false },
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.warn('[files] list failed', target, path, err)
      set(state => ({
        loading: { ...state.loading, [k]: false },
        error: { ...state.error, [k]: message },
      }))
    }
  },
  // Total bytes stored in a target, summed from ONE Depth: infinity PROPFIND.
  //
  // This is the size right NOW, which is deliberately not the number billing
  // uses: `network_metrics.storage_bytes` is a period PEAK sampled off the
  // bucket, and there is no node equivalent at all (router metrics/storage.go
  // sums only the networks/ prefix — node storage needs its own meter, NF-13).
  // Walking the tree is the only figure available for both trees, and one
  // meaning on both pages beats a billing peak on one and nothing on the other.
  //
  // ⚠ It costs a full server-side tree walk against the S3-backed mount, so it
  // is fetched once per target per session and never refetched on navigation —
  // the cache is invalidated only where bytes actually move (upload, remove).
  // Failures are swallowed: the size is decoration, and losing it must never
  // cost the caller its Files link.
  loadSize: async target => {
    const k = targetKey(target)
    // Guard synchronously, before the await: React StrictMode double-invokes
    // effects, and two concurrent walks of the same tree is exactly the cost
    // this is trying to pay only once.
    if (get().sizes[k] !== undefined || get().sizesLoading[k]) return
    const walk = ++sizeWalkSeq
    set(state => ({ sizesLoading: { ...state.sizesLoading, [k]: walk } }))
    try {
      const client = createDavClient(target)
      const stats = await client.getDirectoryContents('/', {
        deep: true,
        signal: AbortSignal.timeout(SIZE_WALK_TIMEOUT_MS),
      })
      const total = stats.reduce((sum, s) => (s.type === 'directory' ? sum : sum + (s.size ?? 0)), 0)
      // Only if this target is still waiting on THIS walk: an upload or a delete
      // in the meantime dropped the id, and the total predates those bytes.
      if (get().sizesLoading[k] !== walk) return
      set(state => ({ sizes: { ...state.sizes, [k]: total } }))
    } catch (err) {
      console.warn('[files] size failed', target, err)
    } finally {
      set(state => {
        if (state.sizesLoading[k] !== walk) return state
        const sizesLoading = { ...state.sizesLoading }
        delete sizesLoading[k]
        return { sizesLoading }
      })
    }
  },
  upload: async (target, path, file) => {
    const id = createId()
    const k = key(target, path)
    const initial: UploadState = { id, basename: file.name, bytesUploaded: 0, bytesTotal: file.size }
    set(state => ({
      uploads: { ...state.uploads, [k]: [...(state.uploads[k] ?? []), initial] },
    }))

    const removeSelf = () => {
      set(state => {
        const next = (state.uploads[k] ?? []).filter(u => u.id !== id)
        const uploads = { ...state.uploads }
        if (next.length) uploads[k] = next
        else delete uploads[k]
        return { uploads }
      })
    }

    // Throttle progress updates: coalesce to ~200 store writes per upload
    // (0.5% of total) to avoid re-render storms on fast connections.
    let lastReported = 0
    try {
      await davPut(target, joinDavPath(path, file.name), file, (loaded, total) => {
        const done = total > 0 && loaded >= total
        if (!done && loaded - lastReported < total / 200) return
        lastReported = loaded
        set(state => ({
          uploads: {
            ...state.uploads,
            [k]: (state.uploads[k] ?? []).map(u =>
              u.id === id ? { ...u, bytesUploaded: loaded } : u
            ),
          },
        }))
      })
    } finally {
      removeSelf()
      // Unconditional: a PUT that threw may still have landed bytes, and a
      // stale total is worse than one re-walk on the next visit.
      set(state => dropSize(state, target))
    }
    // Caller is responsible for refreshing (allows batching multiple uploads).
  },
  download: async (target, filePath, basename) => {
    const client = createDavClient(target)
    const data = (await client.getFileContents(joinDavPath(filePath), { format: 'binary' })) as ArrayBuffer
    const blob = new Blob([data])
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = basename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  },
  remove: async (target, filePath) => {
    const client = createDavClient(target)
    await client.deleteFile(joinDavPath(filePath))
    set(state => dropSize(state, target))
    await get().refresh(target, parentDavPath(filePath))
  },
  createFolder: async (target, path, name) => {
    const client = createDavClient(target)
    await client.createDirectory(joinDavPath(path, name))
    await get().refresh(target, path)
  },
  rename: async (target, filePath, newBasename) => {
    const client = createDavClient(target)
    const parent = parentDavPath(filePath)
    await client.moveFile(joinDavPath(filePath), joinDavPath(parent, newBasename))
    await get().refresh(target, parent)
  },
  reset: () => set({ entries: {}, loading: {}, error: {}, uploads: {}, sizes: {}, sizesLoading: {} }),
}))

export default useFiles
export { key as filesKey }
